import * as zod from "zod";
import { metaStore, primaryKey } from "zodbase";

/**
 * The prefix of every kind of object customers can see, so an ID says what it identifies:
 * `proj_8fK2mQx7Lp4NvB3tRz9Y` is a project. Rows that never leave the backend (usage meters, upload
 * token uses, retained variants, terms acceptances, notices) keep their own internal IDs.
 */
export const ID_PREFIXES = {
  organisation: "org",
  project: "proj",
  asset: "asset",
  folder: "fldr",
  lens: "lens",
  apiKey: "key",
  uploadPreset: "preset",
  connector: "conn",
  connectorDelivery: "dlv",
  hashList: "hl",
  hashListEntry: "hle",
  policy: "pol",
  event: "evt",
  user: "user",
  member: "mem",
  invitation: "inv",
  supportTicket: "tkt",
  supportTicketMessage: "msg",
  humanReview: "rev",
  appeal: "apl",
  complianceCase: "case",
  safetyHold: "hold",
  creditPurchase: "cp",
  oauthClient: "oac",
  waitlistEntry: "wl",
} as const;

export type IdKind = keyof typeof ID_PREFIXES;
export type IdPrefix = (typeof ID_PREFIXES)[IdKind];
/** An ID with its prefix, such as `Id<"proj">`. */
export type Id<P extends IdPrefix = IdPrefix> = `${P}_${string}`;

const ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
/** Random base62 characters after the prefix: about 119 bits. */
export const ID_RANDOM_LENGTH = 20;
const ID_BODY = `[0-9A-Za-z]{${ID_RANDOM_LENGTH}}`;
const ID_PREFIX_VALUES = new Set<string>(Object.values(ID_PREFIXES));

const patterns = new Map<string, RegExp>();
/** Matches one ID with `prefix`, or any registered prefix when none is given. */
export const idPattern = (prefix?: IdPrefix): RegExp => {
  const key = prefix ?? "";
  let pattern = patterns.get(key);
  if (!pattern) {
    const prefixes = prefix ? prefix : [...ID_PREFIX_VALUES].join("|");
    pattern = new RegExp(`^(?:${prefixes})_${ID_BODY}$`);
    patterns.set(key, pattern);
  }
  return pattern;
};

/** Base62 characters from a CSPRNG, without modulo bias (bytes 248-255 are redrawn). */
export const randomBase62 = (length: number): string => {
  let result = "";
  while (result.length < length) {
    const bytes = crypto.getRandomValues(new Uint8Array(length * 2));
    for (const byte of bytes) {
      if (byte >= 248) continue;
      result += ALPHABET[byte % 62];
      if (result.length === length) break;
    }
  }
  return result;
};

/** A new random ID, such as `newId("proj")` → `proj_8fK2mQx7Lp4NvB3tRz9Y`. */
export const newId = <P extends IdPrefix>(prefix: P): Id<P> =>
  `${prefix}_${randomBase62(ID_RANDOM_LENGTH)}`;

/** Bytes `idFromBytes` reads: 112 bits, which 20 base62 characters (about 119 bits) hold whole. */
export const ID_DERIVED_BYTES = 14;

/**
 * An ID derived from the first 14 `bytes` (such as a SHA-256 digest), for IDs that must be the same
 * every time they are computed from the same input (a project's in-app connector).
 */
export const idFromBytes = <P extends IdPrefix>(prefix: P, bytes: Uint8Array): Id<P> => {
  if (bytes.length < ID_DERIVED_BYTES)
    throw new RangeError(`idFromBytes needs at least ${ID_DERIVED_BYTES} bytes`);
  let value = 0n;
  for (const byte of bytes.subarray(0, ID_DERIVED_BYTES)) value = (value << 8n) | BigInt(byte);
  let body = "";
  for (let index = 0; index < ID_RANDOM_LENGTH; index++) {
    body = ALPHABET[Number(value % 62n)] + body;
    value /= 62n;
  }
  return `${prefix}_${body}`;
};

/** Whether `value` is an ID with `prefix` (or with any registered prefix). */
export const isId = <P extends IdPrefix = IdPrefix>(value: unknown, prefix?: P): value is Id<P> =>
  typeof value === "string" && idPattern(prefix).test(value);

/** The prefix of an ID, or undefined when `value` isn't one. */
export const idPrefixOf = (value: string): IdPrefix | undefined =>
  isId(value) ? (value.slice(0, value.indexOf("_")) as IdPrefix) : undefined;

/** Example IDs for docs, OpenAPI and SDK fixtures: fixed, so generated output stays stable. */
export const EXAMPLE_IDS: { [P in IdPrefix]: Id<P> } = {
  org: "org_3hQ7tLk2Vb9XpR4mWc8N",
  proj: "proj_8fK2mQx7Lp4NvB3tRz9Y",
  asset: "asset_5Gm2Rk8XqT3wLp9VbN4c",
  fldr: "fldr_7Nc4Wq2Lm8Xt5Rk9Bv3P",
  lens: "lens_2Xr9Lp4Vm7Qk3Tb8Nw5C",
  key: "key_9Pt3Wm6Rk2Lx8Qv4Nb7C",
  preset: "preset_4Lk8Qw2Xm9Rt5Vb3Nc7P",
  conn: "conn_6Rb2Xk9Lm4Qt7Wp3Nv8C",
  dlv: "dlv_3Vm8Qk2Xt7Lp9Rb4Nw6C",
  hl: "hl_5Xk3Rm9Lq2Vt8Wb4Np7C",
  hle: "hle_8Qm4Xk2Rt9Lv3Wb7Np5C",
  pol: "pol_2Wk7Rm4Xq9Lt3Vb8Np6C",
  evt: "evt_7Lm3Xq8Rk2Vt9Wb4Np5C",
  user: "user_4Rk9Xm2Lq7Vt3Wb8Np6C",
  mem: "mem_9Xm4Rk7Lq2Vt8Wb3Np5C",
  inv: "inv_3Qk8Xm2Rt9Lv4Wb7Np6C",
  tkt: "tkt_6Xm2Rk9Lq4Vt7Wb3Np8C",
  msg: "msg_2Rk7Xm4Lq9Vt3Wb8Np5C",
  rev: "rev_8Xm3Rk2Lq7Vt9Wb4Np6C",
  apl: "apl_4Qk9Xm7Rt2Lv8Wb3Np5C",
  case: "case_7Xm2Rk4Lq9Vt3Wb8Np6C",
  hold: "hold_3Rk8Xm2Lq7Vt4Wb9Np5C",
  cp: "cp_9Qk4Xm7Rt3Lv8Wb2Np6C",
  oac: "oac_5Xm9Rk3Lq2Vt7Wb4Np8C",
  wl: "wl_2Qk7Xm4Rt9Lv3Wb8Np5C",
};

/**
 * A string field holding an ID with `prefix`: a string schema with a pattern, so OpenAPI and the
 * docs show the pattern and the example. Parsed values are typed `string`, not `Id<P>`: typing
 * every stored model's IDs by prefix is a separate change (it surfaces about a thousand call sites
 * across the packages that build records from plain strings).
 */
export const idField = <P extends IdPrefix>(prefix: P) =>
  zod
    .string()
    .regex(idPattern(prefix), { message: `Must be a valid ID starting with "${prefix}_"` })
    .meta({ examples: [EXAMPLE_IDS[prefix]] });

/** The primary key of a table whose rows have IDs with `prefix`. */
export const primaryIdField = <P extends IdPrefix>(prefix: P) =>
  idField(prefix).meta({ ...metaStore([primaryKey()]), examples: [EXAMPLE_IDS[prefix]] });
